import type { SelectChangeEvent } from '@mui/material';
import { MenuItem, Select, Skeleton, Stack, TextField, Typography } from '@mui/material';
import { netsuiteSyncWizardSelectors } from '@vy/app-selectors';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import {
  integrationSelectSx,
  integrationTextareaSx,
} from '../../../../../../../components/integrations/integration-field-styles';
import { useNetSuiteUsers } from '../../../../../../../hooks/requests/integrations';
import { STEP_TITLE_SX } from '../constants/net-suite-sync-wizard-styles';

type SubsidiarySelectionStepProps = {
  erpId: string;
  selectedSubsidiary: string;
  onSubsidiarySelect: (id: string) => void;
};

export type SubsidiarySelectionStepRef = {
  getMemo: () => string;
};

export const SubsidiarySelectionStep = forwardRef<SubsidiarySelectionStepRef, SubsidiarySelectionStepProps>(
  ({ erpId, selectedSubsidiary, onSubsidiarySelect }, ref) => {
    const memoInputRef = useRef<HTMLTextAreaElement>(null);

    const { data, isLoading } = useNetSuiteUsers(erpId, {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });

    const subsidiaries = data?.subsidiaries ?? [];
    const hasOnlyOneSubsidiary = subsidiaries.length === 1;

    useImperativeHandle(ref, () => ({ getMemo: () => memoInputRef.current?.value ?? '' }), []);

    useEffect(() => {
      if (subsidiaries.length > 0 && !selectedSubsidiary) {
        onSubsidiarySelect(subsidiaries[0].id);
      }
    }, [subsidiaries, selectedSubsidiary, onSubsidiarySelect]);

    const handleSubsidiaryChange = (event: SelectChangeEvent<string>) => {
      onSubsidiarySelect(event.target.value);
    };

    return (
      <Stack data-vy={netsuiteSyncWizardSelectors.subsidiaryStep} sx={{
        gap: 3
      }}>
        <Stack sx={{
          gap: 1
        }}>
          <Typography sx={STEP_TITLE_SX}>Next, select a related subsidiary</Typography>

          {isLoading ? (
            <Skeleton variant="rounded" height={48} />
          ) : (
            <Select
              data-vy={netsuiteSyncWizardSelectors.subsidiarySelect}
              value={selectedSubsidiary}
              onChange={handleSubsidiaryChange}
              disabled={hasOnlyOneSubsidiary}
              displayEmpty
              sx={integrationSelectSx}
            >
              {subsidiaries.map((subsidiary) => (
                <MenuItem key={subsidiary.id} value={subsidiary.id}>
                  {subsidiary.name}
                </MenuItem>
              ))}
            </Select>
          )}
        </Stack>
        <Stack sx={{
          gap: 1
        }}>
          <Typography sx={STEP_TITLE_SX}>Add a memo (Optional)</Typography>

          <TextField
            inputRef={memoInputRef}
            variant="outlined"
            multiline
            rows={5}
            placeholder="Enter a note here..."
            sx={integrationTextareaSx}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </Stack>
      </Stack>
    );
  },
);
